import { Component, EventEmitter, Input, OnInit, Output, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';
import { PaymentMethodDto, PaymentMethodClient } from '../../api-connector/sso-connector';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { DrawerService } from '../../components/drawer';
import { PaymentMethodDialogComponent } from '../../components/dialogs/payment-method-dialog/payment-method-dialog.component';
import { AlertService } from '../../services/alert.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
    selector: 'app-payment-method-control',
    templateUrl: './payment-method-control.component.html',
    styleUrls: ['./payment-method-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PaymentMethodControlComponent),
            multi: true
        }
    ]
})
export class PaymentMethodControlComponent implements OnInit, ControlValueAccessor {
    @Input() label = 'Phương thức thanh toán';
    @Input() required = false;
    @Input() submitted = false;
    @Input() placeholder = '-- Chọn phương thức --';

    @Output() paymentMethodsLoaded = new EventEmitter<PaymentMethodDto[]>();

    control = new FormControl(null);
    paymentMethods: PaymentMethodDto[] = [];
    isLoading = false;

    // Cache - simple in-memory cache for this control instance's lifetime or shared if moved to service
    // For now, we use the client directly but we could inject ExpenseService if we want shared cache across components
    // simpler for "Common" control to depend on Client or a Shared Service.
    // We will simple fetch from client for now.

    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(
        private paymentClient: PaymentMethodClient,
        private drawerService: DrawerService,
        private alertService: AlertService,
        private spinner: NgxSpinnerService
    ) { }

    ngOnInit(): void {
        if (this.required) {
            this.control.setValidators(Validators.required);
        }
        this.loadPaymentMethods();

        this.control.valueChanges.subscribe(val => {
            this.onChange(val);
            this.onTouched();
        });
    }

    loadPaymentMethods(forceRefresh = false): void {
        this.isLoading = true;
        this.paymentClient.getAllPaymentMethods(true).pipe(
            tap(methods => {
                this.paymentMethods = methods || [];
                this.paymentMethodsLoaded.emit(this.paymentMethods);
            }),
            catchError(err => {
                console.error(err);
                this.alertService.error('Không thể tải danh sách phương thức thanh toán');
                return of([]);
            }),
            tap(() => this.isLoading = false)
        ).subscribe();
    }

    addPaymentMethod(): void {
        const drawerRef = this.drawerService.open(PaymentMethodDialogComponent, {
            title: 'Thêm phương thức mới',
            width: '500px',
            data: { mode: 'add' }
        });

        drawerRef.afterClosed().subscribe((res: any) => {
            if (res?.success) {
                this.loadPaymentMethods();
                if (res.id) {
                    this.control.setValue(res.id);
                }
            }
        });
    }

    editPaymentMethod(): void {
        const methodId = this.control.value;
        if (!methodId) return;

        const method = this.paymentMethods.find(m => m.id === methodId);
        if (!method) return;

        const drawerRef = this.drawerService.open(PaymentMethodDialogComponent, {
            title: 'Cập nhật phương thức',
            width: '500px',
            data: { mode: 'edit', method }
        });

        drawerRef.afterClosed().subscribe((res: any) => {
            if (res?.success) {
                this.loadPaymentMethods();
            }
        });
    }

    deletePaymentMethod(): void {
        const methodId = this.control.value;
        if (!methodId) return;

        const method = this.paymentMethods.find(m => m.id === methodId);
        if (!method) return;

        this.alertService.confirm(`Bạn có chắc muốn xóa phương thức <b>${method.methodName}</b>?`).subscribe((ok: boolean) => {
            if (ok) {
                this.spinner.show();
                // NOTE: We need delete method. The client might not have it yet if not regenerated, 
                // but user said they added it. We'll assume the client has it or we can cast to any if needed temporarily
                // until strict typing is available.
                // Actually, the user updated `PaymentMethodService` in backend, but client needs regeneration.
                // The user manually added `createPaymentMethod` etc in `sso-connector.ts` in previous turn.
                // So we can use `(this.paymentClient as any).deletePaymentMethod(methodId)` or check if it exists.
                // Since `sso-connector` was updated by user, let's use it.
                (this.paymentClient as any).deletePaymentMethod(methodId)
                    .pipe(tap(() => this.spinner.hide()))
                    .subscribe(() => {
                        this.alertService.success('Xóa phương thức thành công');
                        this.loadPaymentMethods();
                        this.control.setValue(null);
                    }, () => this.spinner.hide());
            }
        });
    }

    // ControlValueAccessor
    writeValue(obj: any): void {
        this.control.setValue(obj);
    }
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        if (isDisabled) this.control.disable();
        else this.control.enable();
    }

    hasError(error: string): boolean {
        return this.control.hasError(error) && (this.control.dirty || this.control.touched || this.submitted);
    }
}
